import {
  ActionIcon,
  Badge,
  Box,
  Button,
  Card,
  Divider,
  FileButton,
  Group,
  Modal,
  Paper,
  ScrollArea,
  SegmentedControl,
  Select,
  SimpleGrid,
  Slider,
  Stack,
  Text,
  TextInput,
  Title,
  Tooltip,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import {
  IconAlignCenter,
  IconAlignLeft,
  IconAlignRight,
  IconArrowDown,
  IconArrowUp,
  IconCheck,
  IconEye,
  IconPlus,
  IconSparkles,
  IconTrash,
  IconUpload,
} from '@tabler/icons-react';
import React, { useState } from 'react';

import { parseCustomFontFile } from '../engine/font-loader';
import { CURATED_FONTS } from '../fonts/font-catalog';
import { useVectorCadStore } from '../stores/vector-cad-store';

export function TypographyPanel() {
  const {
    lines,
    customFonts,
    lineSpacingMultiplier,
    overallAlignment,
    selectedLineId,
    addLine,
    updateLine,
    removeLine,
    moveLine,
    selectLine,
    setLineSpacingMultiplier,
    setOverallAlignment,
    addCustomFont,
  } = useVectorCadStore();

  const [galleryOpened, { open: openGallery, close: closeGallery }] = useDisclosure(false);
  const [galleryCategory, setGalleryCategory] = useState<string>('all');
  const [activeLineForGallery, setActiveLineForGallery] = useState<string | null>(null);

  const allFonts = [...CURATED_FONTS, ...customFonts];

  const filteredGalleryFonts = allFonts.filter((f) => {
    if (galleryCategory === 'all') return true;
    return f.category === galleryCategory;
  });

  const fontSelectData = allFonts.map((f) => ({
    value: f.id,
    label: f.name,
  }));

  const handleCustomFontUpload = async (file: File | null) => {
    if (!file) return;
    try {
      const { fontDef } = await parseCustomFontFile(file);
      addCustomFont(fontDef);
    } catch (err) {
      console.error('Failed to parse uploaded font:', err);
    }
  };

  const openFontGalleryForLine = (lineId: string) => {
    setActiveLineForGallery(lineId);
    openGallery();
  };

  const activeLineObj = lines.find((l) => l.id === activeLineForGallery) || lines[0];

  return (
    <Stack gap='md'>
      {/* Header & Alignment Controls */}
      <Group justify='space-between'>
        <Title order={5}>Typography & Quotes</Title>
        <SegmentedControl
          size='xs'
          value={overallAlignment}
          onChange={(val) => setOverallAlignment(val as 'left' | 'center' | 'right')}
          data={[
            { label: <IconAlignLeft size={14} />, value: 'left' },
            { label: <IconAlignCenter size={14} />, value: 'center' },
            { label: <IconAlignRight size={14} />, value: 'right' },
          ]}
        />
      </Group>

      {/* Global Line Spacing */}
      <Box>
        <Group justify='space-between' mb={4}>
          <Text size='xs' c='dimmed'>
            Line Spacing
          </Text>
          <Text size='xs' fw={600}>
            {lineSpacingMultiplier.toFixed(2)}×
          </Text>
        </Group>
        <Slider
          size='sm'
          min={0.8}
          max={2.0}
          step={0.05}
          value={lineSpacingMultiplier}
          onChange={setLineSpacingMultiplier}
        />
      </Box>

      <Divider />

      {/* Text Lines List */}
      <Stack gap='sm'>
        {lines.map((line, idx) => {
          const isSelected = selectedLineId === line.id;
          const currentFont = allFonts.find((f) => f.id === line.fontId) || allFonts[0];

          return (
            <Paper
              key={line.id}
              withBorder
              p='sm'
              radius='md'
              style={{
                borderColor: isSelected ? 'var(--mantine-color-blue-6)' : undefined,
                background: 'var(--mantine-color-dark-7)',
              }}
              onClick={() => selectLine(line.id)}
            >
              <Stack gap='xs'>
                {/* Line Header & Actions */}
                <Group justify='space-between'>
                  <Group gap='xs'>
                    <Badge size='xs' variant='light' color='blue'>
                      Line {idx + 1}
                    </Badge>
                    <Badge size='xs' variant='outline' color='gray'>
                      {currentFont.category}
                    </Badge>
                  </Group>
                  <Group gap={4}>
                    <ActionIcon
                      variant='subtle'
                      size='xs'
                      disabled={idx === 0}
                      onClick={(e) => {
                        e.stopPropagation();
                        moveLine(line.id, 'up');
                      }}
                    >
                      <IconArrowUp size={14} />
                    </ActionIcon>
                    <ActionIcon
                      variant='subtle'
                      size='xs'
                      disabled={idx === lines.length - 1}
                      onClick={(e) => {
                        e.stopPropagation();
                        moveLine(line.id, 'down');
                      }}
                    >
                      <IconArrowDown size={14} />
                    </ActionIcon>
                    <ActionIcon
                      variant='subtle'
                      color='red'
                      size='xs'
                      disabled={lines.length <= 1}
                      onClick={(e) => {
                        e.stopPropagation();
                        removeLine(line.id);
                      }}
                    >
                      <IconTrash size={14} />
                    </ActionIcon>
                  </Group>
                </Group>

                {/* Text Input */}
                <TextInput
                  placeholder='Enter quote or text...'
                  value={line.text}
                  onChange={(e) => updateLine(line.id, { text: e.target.value })}
                  size='sm'
                />

                {/* Font Selector & Gallery Button */}
                <Group gap='xs' align='flex-end'>
                  <Box style={{ flex: 1 }}>
                    <Select
                      size='xs'
                      label='Font Family'
                      data={fontSelectData}
                      value={line.fontId}
                      onChange={(val) => val && updateLine(line.id, { fontId: val })}
                      searchable
                      clearable={false}
                      renderOption={({ option }) => {
                        const f = allFonts.find((item) => item.id === option.value);
                        return (
                          <Group justify='space-between' wrap='nowrap' py={2}>
                            <Text
                              style={{
                                fontFamily: f?.family || 'inherit',
                                fontSize: 16,
                              }}
                            >
                              {f?.name}
                            </Text>
                            <Badge size='xs' variant='light' color='gray'>
                              {f?.category}
                            </Badge>
                          </Group>
                        );
                      }}
                    />
                  </Box>
                  <Tooltip label='Open Visual Font Gallery'>
                    <Button
                      size='xs'
                      variant='light'
                      color='blue'
                      leftSection={<IconEye size={14} />}
                      onClick={(e) => {
                        e.stopPropagation();
                        openFontGalleryForLine(line.id);
                      }}
                    >
                      Preview All
                    </Button>
                  </Tooltip>
                </Group>

                {/* Font Size Slider */}
                <Box>
                  <Group justify='space-between' mb={2}>
                    <Text size='xs' c='dimmed'>
                      Font Size
                    </Text>
                    <Text size='xs' fw={600}>
                      {line.fontSizeMm} mm
                    </Text>
                  </Group>
                  <Slider
                    size='xs'
                    min={10}
                    max={120}
                    step={1}
                    value={line.fontSizeMm}
                    onChange={(val) => updateLine(line.id, { fontSizeMm: val })}
                  />
                </Box>

                {/* Letter Overlap / Spacing */}
                <Box>
                  <Group justify='space-between' mb={2}>
                    <Text size='xs' c='dimmed'>
                      Letter Spacing / Overlap
                    </Text>
                    <Text size='xs' fw={600} c={line.letterSpacingPercent < 0 ? 'blue' : undefined}>
                      {line.letterSpacingPercent}%{' '}
                      {line.letterSpacingPercent < 0 ? '(Cursive Weld)' : ''}
                    </Text>
                  </Group>
                  <Slider
                    size='xs'
                    min={-15}
                    max={25}
                    step={1}
                    value={line.letterSpacingPercent}
                    onChange={(val) => updateLine(line.id, { letterSpacingPercent: val })}
                    marks={[
                      { value: -4, label: 'Weld' },
                      { value: 0, label: '0' },
                      { value: 10, label: '+10%' },
                    ]}
                  />
                </Box>
              </Stack>
            </Paper>
          );
        })}
      </Stack>

      {/* Add Line & Upload Custom Font Buttons */}
      <Group grow>
        <Button
          leftSection={<IconPlus size={16} />}
          variant='light'
          size='xs'
          onClick={() => addLine('New Quote Line')}
        >
          Add Text Line
        </Button>

        <FileButton onChange={handleCustomFontUpload} accept='.ttf,.otf,.woff'>
          {(props) => (
            <Button
              {...props}
              leftSection={<IconUpload size={16} />}
              variant='subtle'
              size='xs'
              color='gray'
            >
              Upload Font
            </Button>
          )}
        </FileButton>
      </Group>

      {/* Visual Font Gallery Modal */}
      <Modal
        opened={galleryOpened}
        onClose={closeGallery}
        title={
          <Group gap='xs'>
            <IconSparkles size={18} color='var(--mantine-color-blue-4)' />
            <Text fw={600}>Visual Font Gallery</Text>
            <Badge size='xs' color='blue'>
              Live Preview: "{activeLineObj?.text || 'Sample Text'}"
            </Badge>
          </Group>
        }
        size='xl'
        centered
      >
        <Stack gap='md'>
          {/* Category Filter */}
          <SegmentedControl
            size='xs'
            value={galleryCategory}
            onChange={setGalleryCategory}
            data={[
              { label: 'All Fonts', value: 'all' },
              { label: 'Script / Cursive', value: 'script' },
              { label: 'Display', value: 'display' },
              { label: 'Serif', value: 'serif' },
              { label: 'Sans-Serif', value: 'sans' },
            ]}
          />

          {/* Responsive Font Preview Grid */}
          <ScrollArea h={420} offsetScrollbars>
            <SimpleGrid cols={{ base: 1, sm: 2 }} spacing='sm'>
              {filteredGalleryFonts.map((f) => {
                const isCurrent = activeLineObj?.fontId === f.id;
                return (
                  <Card
                    key={f.id}
                    withBorder
                    p='sm'
                    radius='md'
                    style={{
                      cursor: 'pointer',
                      borderColor: isCurrent ? 'var(--mantine-color-blue-5)' : undefined,
                      background: isCurrent
                        ? 'rgba(34, 139, 230, 0.12)'
                        : 'var(--mantine-color-dark-6)',
                      transition: 'all 0.15s ease',
                    }}
                    onClick={() => {
                      if (activeLineForGallery) {
                        updateLine(activeLineForGallery, { fontId: f.id });
                      }
                      closeGallery();
                    }}
                  >
                    <Stack gap='xs'>
                      <Group justify='space-between'>
                        <Text size='xs' fw={600} c='dimmed'>
                          {f.name}
                        </Text>
                        <Group gap={4}>
                          <Badge size='xs' variant='light' color='gray'>
                            {f.category}
                          </Badge>
                          {isCurrent && (
                            <Badge size='xs' color='blue' leftSection={<IconCheck size={12} />}>
                              Selected
                            </Badge>
                          )}
                        </Group>
                      </Group>

                      {/* Live Text in Real Font Family */}
                      <Box py='xs' px={4} style={{ overflow: 'hidden' }}>
                        <Text
                          style={{
                            fontFamily: f.family,
                            fontSize: 26,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            color: '#ffffff',
                            lineHeight: 1.2,
                          }}
                        >
                          {activeLineObj?.text || 'Always & Forever'}
                        </Text>
                      </Box>
                    </Stack>
                  </Card>
                );
              })}
            </SimpleGrid>
          </ScrollArea>
        </Stack>
      </Modal>
    </Stack>
  );
}
